/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/window.d.ts" />

/**
 * Health service
 */

namespace Application.Services {
    'use strict';

    import NodeHealth = Models.NodeHealth;
    import INodeHealth = Models.INodeHealth;
    import IDataNodeHealth = Models.IDataNodeHealth;
    import IHalList = Models.IHalList;
    import HalConverter = Models.HalConverter;
    import ITCPPing = Models.ITCPPing;

    export interface IDataNodeHealthMap {
        [key: string]: Models.IDataNodeHealth;
    }

    /**
     * HealthService
     */
    export class HealthService {

        public static $name: string = "healthService";
        public static $inject: string[] = ['$rootScope',
                                           '$http',
                                           'stompService'];
        private static HEALTH_MAP_CHANGED: string = 'health-map-changed';

        public nodeHealthMap: IDataNodeHealthMap = {};
        private backendBaseUrl: string = window.backendUrl;
        private servicePath: string = ''; // 'nodehealth/';
        private backendUrl: string = this.backendBaseUrl + this.servicePath + 'healthChecks';

        constructor(private $rootScope: ng.IRootScopeService,
                    private $http: ng.IHttpService,
                    private stompService: StompService) {
            this.$rootScope.$on('vars.isUserLoggedIn', (event: ng.IAngularEvent, args: boolean) => {
                if (args) {
                    this.getNodeHealthList();
                }
            });
            this.initStompSubscriptions();
        }

        public subscribeOnChangedHealth(scope: ng.IScope, callback: (event: angular.IAngularEvent, args: any[]) => void): void { //tslint:disable-line
            let handler: Function = this.$rootScope.$on(HealthService.HEALTH_MAP_CHANGED, callback);
            scope.$on('$destroy', handler as (event: ng.IAngularEvent, ...args: any[]) => any); //tslint:disable-line
        }

        public getNetworkNodeHealth(id: string): IDataNodeHealth {
            return this.getNodeHealth("/networkNodes/" + id);
        }

        public getNodeHealth(href: string): IDataNodeHealth {
            let health: IDataNodeHealth = this.nodeHealthMap[Common.Utils.getSimplifiedHrefFromString(href)];
            if (health === undefined) {
                this.nodeHealthMap[Common.Utils.getSimplifiedHrefFromString(href)] = {
                    data : {
                        elementId: Common.Utils.getSimplifiedHrefFromString(href),
                        checks : [],
                        healthStatus : "PENDING",
                    },
                };
            }
            return this.nodeHealthMap[Common.Utils.getSimplifiedHrefFromString(href)];
        }

        public refreshNodeHealth(href: string): void {
            this.$http.get(this.backendUrl + Common.Utils.getSimplifiedHrefFromString(href)).then(
                (callback: ng.IHttpPromiseCallbackArg<INodeHealth>) => {
                    if (callback.data && this.nodeHealthMap[callback.data.elementId]) {
                        this.reloadNodeHealth(callback.data);
                    }
                });
        }

        public addTcpPingCheckForElement(element: Models.ITopologyElement, check: ITCPPing): ng.IPromise<IDataNodeHealth> {
            return this.$http.post(this.backendUrl + "/tcpPings",  check).then(
                (callback: ng.IHttpPromiseCallbackArg<INodeHealth>) => {
                    let href: string = Common.Utils.getSimplifiedHrefFromString(element.href);
                    return this.nodeHealthMap[href];
                }
            );
        }

        public editTcpPingCheckForElement(element: Models.ITopologyElement, check: ITCPPing, checkId: string): ng.IPromise<INodeHealth> {
            let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(element.href);
            return this.$http.put(this.backendUrl + "/tcpPings" + simplifiedHref + "/" + checkId,  check).then(
                (callback: ng.IHttpPromiseCallbackArg<INodeHealth>) => {                    
                    return this.reloadNodeHealth(callback.data);
                }
            );
        }

        public removeTcpPing(element: Models.ITopologyElement, checkId: string): ng.IPromise<INodeHealth> {
            let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(element.href);
            return this.$http.delete(this.backendUrl + "/tcpPings" + simplifiedHref + "/" + checkId).then(
                (callback: ng.IHttpPromiseCallbackArg<INodeHealth>) => {
                    return this.reloadNodeHealth(callback.data);
                });
        }

        private getNodeHealthList(): void {
            this.getNodeHealthListPromise().then(
                (result: INodeHealth[]) => {
                    result.forEach (
                        (nodeHealth: INodeHealth) => {
                            this.nodeHealthMap[nodeHealth.elementId] = { data : nodeHealth } ;
                        }
                    );
                });
        }

        private getNodeHealthListPromise(): ng.IPromise<INodeHealth[]> {
            return this.$http.get(this.backendUrl).then(
                (result: ng.IHttpPromiseCallbackArg<IHalList<INodeHealth>>) => {
                    return HalConverter.convertList(result.data, NodeHealth.Empty);
                });
        }

        private updateHealthChanged(flexEvent: Models.IFlexEvent): void {
            let nodeHealth: INodeHealth = flexEvent.data as INodeHealth;
            let previousHealth: INodeHealth = this.nodeHealthMap[nodeHealth.elementId] ? 
                this.nodeHealthMap[nodeHealth.elementId].data : undefined;
            if (!previousHealth) {
                this.nodeHealthMap[nodeHealth.elementId] = { data: nodeHealth };
            } else if (previousHealth.healthStatus !== nodeHealth.healthStatus || 
                       this.someHealthChecksChanged(previousHealth, nodeHealth)) {
                this.nodeHealthMap[nodeHealth.elementId].data = nodeHealth;
            }
        }

        private someHealthChecksChanged(previous: INodeHealth, current: INodeHealth): boolean {
            let changedByCheckIdAndHealthStatus: boolean = current.checks.some((currentCheck: Models.INodeCheck) => {
                return previous.checks.some((oldCheck: Models.INodeCheck) => {
                    return currentCheck.checkId === oldCheck.checkId && currentCheck.healthStatus !== oldCheck.healthStatus;
                });
            });

            let healthChangedByNameAndHealthStatus: boolean = current.checks.some((currentCheck: Models.INodeCheck) => {
                return previous.checks.some((oldCheck: Models.INodeCheck) => {
                    return currentCheck.name === oldCheck.name && currentCheck.healthStatus !== oldCheck.healthStatus;
                });
            });

            let changedChecksLength: boolean = current.checks.length !== previous.checks.length;

            return changedByCheckIdAndHealthStatus || healthChangedByNameAndHealthStatus || changedChecksLength;
        }

        private reloadNodeHealth(iNodeHealth: INodeHealth): Models.NodeHealth {
            let nodeHealth: Models.NodeHealth = new Models.NodeHealth();                    
            nodeHealth.checks = iNodeHealth.checks;
            nodeHealth.elementId = iNodeHealth.elementId;
            nodeHealth.healthStatus = iNodeHealth.healthStatus;
            this.nodeHealthMap[nodeHealth.elementId].data = nodeHealth;
            return nodeHealth;
        }

        private notifyHealthChanged(flexEvent: Models.IFlexEvent): void {
            this.$rootScope.$emit(HealthService.HEALTH_MAP_CHANGED, [flexEvent]);
        }

        private initStompSubscriptions(): void {
            this.stompService.register(Models.NODE_HEALTH_COMPONENT, Models.HEALTH_CHANGED,
                    (flexEvent: Models.IFlexEvent) => {
                        this.updateHealthChanged(flexEvent);
                        this.notifyHealthChanged(flexEvent);
                    }
            );

            this.stompService.register(Models.NETWORK_NODES_COMPONENT, Models.DELETE_NETWORK_NODE_OPERATION, 
                (flexEvent: Models.IFlexEvent) => {
                    let deletedNetworkNode: Models.INetworkNode = flexEvent.data as Models.INetworkNode;
                    delete this.nodeHealthMap['/networkNodes/' + deletedNetworkNode.id];
                }
            );

            this.stompService.register(Models.NETWORK_NODES_COMPONENT, Models.DELETE_GATEWAY_OPERATION, 
                (flexEvent: Models.IFlexEvent) => {
                    let deletedGateway: Models.IGateway = flexEvent.data as Models.IGateway;
                    if (deletedGateway.href !== undefined)
                        delete this.nodeHealthMap['/gateways/' + Common.Utils.idFromHref(deletedGateway.href)];
                    else
                        delete this.nodeHealthMap['/gateways/' + deletedGateway.id];
                }
            );
        }
    }
}